import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useApp, API } from "../context/AppContext";
import PlanForm from "../components/PlanForm";
import PlanCard from "../components/PlanCard";
import NaturalLanguageInput from "../components/NaturalLanguageInput";
import { Sparkles, Loader2, RefreshCw, Users2, CloudRain, Sun, Thermometer } from "lucide-react";

export default function Home() {
  const { t, user, axiosOpts } = useApp();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [origin, setOrigin] = useState("");
  const [lastReq, setLastReq] = useState(null);
  const [sharing, setSharing] = useState(false);
  const formRef = useRef(null);

  const handleParsed = (data) => {
    formRef.current?.applyParsed(data);
    // Scroll form into view for visual confirmation
    setTimeout(() => {
      document.querySelector('[data-testid="plan-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const runGenerate = async (payload) => {
    setLoading(true);
    setResult(null);
    setOrigin(payload.location);
    setLastReq(payload);
    try {
      const { data } = await axios.post(`${API}/plans/generate`, payload);
      setResult(data);
      setSavedIds(new Set());
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 || detail === "LLM_BUDGET_EXCEEDED") {
        toast.error(t.budgetError, { duration: 10000 });
      } else if (status === 429 || detail === "LLM_RATE_LIMITED") {
        toast.error(t.rateLimitError);
      } else {
        toast.error(t.aiError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (payload) => runGenerate(payload);
  const handleSurprise = (payload) => runGenerate(payload);

  const handleTryAgain = () => {
    if (!lastReq) return;
    const avoid = (result?.plans || []).map((p) => p.title);
    runGenerate({ ...lastReq, avoid_titles: avoid });
  };

  const handleSave = async (plan) => {
    if (!user) return;
    try {
      await axios.post(`${API}/favorites`, { plan, request: result.request }, axiosOpts());
      setSavedIds((prev) => new Set(prev).add(plan.id));
      toast.success(t.saved);
    } catch {
      toast.error("Error");
    }
  };

  const handleGroupShare = async () => {
    if (!result || sharing) return;
    setSharing(true);
    try {
      const { data } = await axios.post(`${API}/plans/share`, {
        plans: result.plans,
        request: result.request,
        creator_name: user?.name || null,
      });
      const url = `${window.location.origin}/g/${data.id}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      const msg = t.lang === "es" || true
        ? `Vota nuestro plan de grupo: ${url}`
        : `Vote our group plan: ${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
      toast.success(t.groupVote);
    } catch {
      toast.error("Error");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="max-w-6xl mx-auto px-5 pt-10 sm:pt-16 pb-8">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-mono font-semibold text-[#2C5E3B] bg-[#EDF7EF] border border-[#C9E5CE] px-3 py-1 rounded-full">
            <Sparkles className="w-3.5 h-3.5" />
            {t.heroBadge}
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-stone-900">
            {t.heroTitle}
          </h1>
          <p className="mt-4 text-lg text-stone-600 leading-relaxed max-w-2xl">{t.tagline}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-16">
        <NaturalLanguageInput onParsed={handleParsed} />
        <PlanForm ref={formRef} onSubmit={handleSubmit} onSurprise={handleSurprise} loading={loading} />
      </section>

      {loading && (
        <section data-testid="loading-state" className="max-w-6xl mx-auto px-5 pb-16 text-center">
          <div className="inline-flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-[#D95338]" />
            <p className="text-sm text-stone-600">{t.generatingSub.replace("{loc}", origin || "...")}</p>
          </div>
        </section>
      )}

      {result && !loading && (
        <section className="max-w-6xl mx-auto px-5 pb-24">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p data-testid="plans-count" className="text-sm font-mono uppercase tracking-wider text-stone-500">
              {t.plansCount(result.plans.length, result.request.people)}
            </p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="button-try-again" onClick={handleTryAgain}
                className="text-xs font-semibold px-4 py-2 rounded-full bg-white border border-[#E7E0D5] hover:border-[#D95338] hover:text-[#D95338] inline-flex items-center gap-1.5 transition-all">
                <RefreshCw className="w-3.5 h-3.5" />
                {t.tryAgain}
              </button>
              <button data-testid="button-group-share" onClick={handleGroupShare} disabled={sharing}
                className="text-xs font-semibold px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-700 inline-flex items-center gap-1.5 transition-all disabled:opacity-60">
                <Users2 className="w-3.5 h-3.5" />
                {sharing ? t.creatingShare : t.groupVote}
              </button>
            </div>
          </div>

          {(result.weather || result.is_holiday) && (
            <div data-testid="context-panel" className="mb-6 p-4 rounded-2xl bg-white border border-[#E7E0D5] flex flex-wrap items-center gap-4">
              {result.weather && (
                <>
                  <span className="inline-flex items-center gap-2 text-sm">
                    {result.weather.is_rainy ? <CloudRain className="w-4 h-4 text-[#B54708]" /> : <Sun className="w-4 h-4 text-[#E09F3E]" />}
                    <strong>{result.weather.label}</strong>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-stone-600">
                    <Thermometer className="w-4 h-4" />
                    {result.weather.temp_min}° – {result.weather.temp_max}°C
                  </span>
                  <span className="text-sm text-stone-600">💧 {result.weather.rain_prob}%</span>
                </>
              )}
              {result.is_holiday && (
                <span className="text-xs font-semibold bg-[#FFF4EC] text-[#B54708] border border-[#FDCC9D] px-2.5 py-1 rounded-full">
                  🎉 {t.holiday}: {result.holiday_name}
                </span>
              )}
            </div>
          )}

          <div className="grid gap-6">
            {result.plans.map((plan, i) => (
              <PlanCard key={plan.id} plan={plan} index={i} onSave={handleSave} saved={savedIds.has(plan.id)} canSave={!!user} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
